/**
 * The moving block's centre position as a PURE FUNCTION of elapsed time —
 * not an accumulated per-frame delta. Mirrors game-core's round.ts, which
 * computes everything from timestamp subtraction rather than mutating
 * accumulators specifically so it's immune to frame-rate variance and
 * trivially testable: the same `elapsedMs` always yields the same position,
 * regardless of how many rendered frames occurred in between.
 *
 * A triangle wave: the block sweeps from `low` to `high` and back,
 * indefinitely, at a constant speed (spec §4 — no randomness in movement
 * timing, so the player's timing skill stays transferable between runs).
 *
 * @param low the centre position when the block is at its leftmost/starting edge.
 * @param high the centre position when the block is at its rightmost edge.
 * @param startEdge which edge the sweep begins from at elapsedMs=0 — alternates
 *   per spawn (spec §5's 2D simplification of the full 3D alternating-axis idea).
 */
export function sweepPosition(
  elapsedMs: number,
  low: number,
  high: number,
  speedUnitsPerSec: number,
  startEdge: 'low' | 'high',
): number {
  const amplitude = high - low;
  if (amplitude <= 0) return low; // defensive: a block wider than the track has nowhere to sweep
  const distance = (Math.max(0, elapsedMs) / 1000) * speedUnitsPerSec;
  const period = 2 * amplitude;
  const phase = distance % period;
  const fromLow = phase <= amplitude ? phase : period - phase;
  return startEdge === 'low' ? low + fromLow : high - fromLow;
}

/** The valid centre-position range for a block of `blockWidth` within `trackWidth` — the block's own edges never travel off-track. */
export function sweepBounds(trackWidth: number, blockWidth: number): { low: number; high: number } {
  const half = blockWidth / 2;
  const low = half;
  const high = Math.max(half, trackWidth - half);
  return { low, high };
}
